// src/redux/reducers.js
import Icons from 'src/components/Icons';
import * as actionTypes from './types';
import {
  ChatListItem,
  ConversationHistoryItem,
  ShareConversationInfo,
  AnswerStatus,
  EnumConversationType,
} from 'src/types/conversation';
import { TopicConfigType } from 'src/pages/Backstage/subPages/DataSource/TopicConfig/types';
import { DEVICE_FINGERPRINT } from 'src/utils/anonymous';

const initialState = {
  // 初次进入对话加载
  loadingChat: false,
  abortController: null,
  // 对话列表分页
  conversationPage: {
    page: 1,
    page_size: 50,
  },
  conversationTotal: 0,
  // 对话列表
  conversationList: [] as ConversationHistoryItem[],
  // 当前对话的id
  conversationId: null as string | null,
  roundId: null as string | null,
  // 聊天分页
  chatPage: {
    page: 1,
    page_size: 30,
  },
  chatTotal: 0,
  // 单个对话内的聊天列表
  chatList: [] as ChatListItem[],
  // 加载单个对话列表
  loadingChatList: false,
  conversationTypeList: [] as {
    type: EnumConversationType;
    typeName: string;
    icon: React.ReactNode;
  }[],
  // 当前对话的type 主题分析 | 综合分析
  conversationType: EnumConversationType.TOPIC as EnumConversationType,
  // 提问内容
  input: '', // 查询每个班级在2023-2024春季学期数学的总评平均分
  // 提问方式 语音 | 文本
  inputType: 'text', // 'voice' | 'text'
  // 回答状态 等待中 | 回答中 |已完成
  answerStatus: 'completed' as AnswerStatus, // 'completed' | 'waiting' | 'answering'
  // 说话状态 说话中true | 未说话false
  speeching: false,
  // 主题分析下 主题类型列表
  topicTypeList: [] as TopicConfigType.DataItem[],
  // 选择的主题类型 0 为综合分析 其他为主题分析
  topicType: 0,
  // 首页推荐的提问列表
  recommendedQuestion: [] as TopicConfigType.QuestionItem[],
  // 是否分享中
  conversationShare: false,
  // 分享的对话
  shareConversation: {
    title: null,
    ids: [],
    user_id: null,
    user_name: null,
  } as ShareConversationInfo,
  // is_error的提示
  errorText:
    '针对您的问题，我将您有权访问的所有数据表像过筛子一样细细筛查了一遍，截至目前，尚未发现与您问题直接相关的数据表。',
  collapsed: false,
  // 缓存的主题
  cacheTopicType: null,
  // 缓存的聊天id
  cacheConversationId: null,
  // 是否已加载过WelcomeTopic
  initWelcomeTopic: false,
  // 是否已加载过WelcomeComprehensive
  initWelcomeComprehensive: false,
  /** 是否开启人机协作模式 */
  collaborate_model: false,
  /** 是否匿名 */
  isAnonymous: false,
  /** 匿名用户id */
  tenant_id: DEVICE_FINGERPRINT,
};

const reducer = (
  state = initialState,
  action: { type: string; payload: any },
) => {
  switch (action.type) {
    case actionTypes.SET_CONVERSATION_LIST:
      return {
        ...state,
        conversationList: action.payload as ConversationHistoryItem[],
      };
    case actionTypes.SET_CONVERSATION_ID:
      return { ...state, conversationId: action.payload as string | null };
    case actionTypes.SET_ROUND_ID:
      return { ...state, roundId: action.payload as string | null };
    case actionTypes.SET_CONVERSATION_TYPE:
      return {
        ...state,
        conversationType: action.payload as EnumConversationType,
      };
    case actionTypes.SET_CHAT_LIST:
      return { ...state, chatList: action.payload as ChatListItem[] };
    case actionTypes.SET_LOADING_CHAT_LIST:
      return { ...state, loadingChatList: action.payload };
    case actionTypes.SET_INPUT:
      return { ...state, input: action.payload };
    case actionTypes.SET_INPUT_TYPE:
      return { ...state, inputType: action.payload };
    case actionTypes.SET_ANSWER_STATUS:
      return { ...state, answerStatus: action.payload as AnswerStatus };
    case actionTypes.SET_SPEECHING:
      return { ...state, speeching: action.payload };
    case actionTypes.SET_CONVERSATION_TYPE_LIST: {
      const typeMap = {
        [EnumConversationType.TOPIC]: {
          type: EnumConversationType.TOPIC,
          typeName: '主题分析',
          icon: <Icons.ChatComprehensive />,
        },
        [EnumConversationType.COMPREHENSIVE]: {
          type: EnumConversationType.COMPREHENSIVE,
          typeName: '综合分析',
          icon: <Icons.ChatTopic />,
        },
      };
      const result = (action.payload as EnumConversationType[])
        .filter(v => typeMap[v])
        .map(v => typeMap[v]);
      return {
        ...state,
        conversationTypeList: result,
        conversationType: result[0].type,
      };
    }
    case actionTypes.SET_TOPIC_TYPE_LIST:
      return {
        ...state,
        topicTypeList: action.payload as TopicConfigType.DataItem[],
      };
    case actionTypes.SET_TOPIC_TYPE:
      return { ...state, topicType: action.payload as number };
    case actionTypes.SET_RECOMMENDED_QUESTION:
      return {
        ...state,
        recommendedQuestion: action.payload as TopicConfigType.QuestionItem[],
      };
    case actionTypes.SET_CONVERSATION_SHARE:
      return { ...state, conversationShare: action.payload };
    case actionTypes.SET_SHARE_CONVERSATION:
      return {
        ...state,
        shareConversation: action.payload as ShareConversationInfo,
      };
    case actionTypes.SET_ABORT_CONTROLLER:
      return { ...state, abortController: action.payload };
    case actionTypes.SET_LOADING_CHAT:
      return { ...state, loadingChat: action.payload };
    case actionTypes.SET_COLLAPSED:
      return { ...state, collapsed: action.payload };
    case actionTypes.SET_CONVERSATION_PAGE:
      return { ...state, conversationPage: action.payload };
    case actionTypes.SET_CONVERSATION_TOTAL:
      return { ...state, conversationTotal: action.payload };
    case actionTypes.SET_CHAT_PAGE:
      return { ...state, chatPage: action.payload };
    case actionTypes.SET_CHAT_TOTAL:
      return { ...state, chatTotal: action.payload };
    case actionTypes.SET_CACHE_CONVERSATION_ID:
      return { ...state, cacheConversationId: action.payload };
    case actionTypes.SET_CACHE_TOPIC_TYPE:
      return { ...state, cacheTopicType: action.payload };
    case actionTypes.SET_INIT_WELCOME_TOPIC:
      return { ...state, initWelcomeTopic: action.payload };
    case actionTypes.SET_INIT_WELCOME_COMPREHENSIVE:
      return { ...state, initWelcomeComprehensive: action.payload };
    case actionTypes.SET_INTERACT:
      return { ...state, collaborate_model: action.payload };
    case actionTypes.SET_IS_ANONYMOUS:
      return { ...state, isAnonymous: action.payload as boolean };
    default:
      return state;
  }
};

export default reducer;
